import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Profile } from "@/integrations/supabase/types/subscription";
import { Bell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface NotificationsCardProps {
  profile: Profile;
  onUpdate: (updates: Partial<Profile>) => Promise<void>;
}

export function NotificationsCard({ profile, onUpdate }: NotificationsCardProps) {
  const { toast } = useToast();
  const [localEmailState, setLocalEmailState] = useState(profile.notification_preferences?.email ?? false);
  const [localPushState, setLocalPushState] = useState(profile.notification_preferences?.push ?? false);

  const handleNotificationChange = async (type: 'email' | 'push', checked: boolean) => {
    // Update local state immediately for instant feedback
    if (type === 'email') {
      setLocalEmailState(checked);
    } else {
      setLocalPushState(checked);
    }

    try {
      await onUpdate({
        notification_preferences: {
          ...profile.notification_preferences,
          [type]: checked,
        },
      });
      
      toast({
        title: "Innstillinger oppdatert",
        description: `${type === 'email' ? 'E-postvarsler' : 'Push-varsler'} er nå ${checked ? 'aktivert' : 'deaktivert'}`,
        variant: "default",
      });
    } catch (error) {
      // Revert local state if update fails
      if (type === 'email') {
        setLocalEmailState(!checked);
      } else {
        setLocalPushState(!checked);
      }
      
      toast({
        title: "Kunne ikke oppdatere innstillinger",
        description: "Prøv igjen senere",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="transition-all duration-300 hover:shadow-lg bg-white/50 backdrop-blur-sm">
      <CardHeader className="space-y-1">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-orange-100">
            <Bell className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <CardTitle className="text-xl text-orange-900">Varsler</CardTitle>
            <CardDescription className="text-sm text-orange-600/80">
              Administrer varslingsinnstillinger
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between space-x-4 p-3 rounded-lg hover:bg-orange-50 transition-colors">
          <div className="space-y-0.5">
            <Label htmlFor="emailNotifications" className="text-orange-900 font-medium">
              E-postvarsler
            </Label>
            <p className="text-sm text-orange-600/80">
              Motta viktige oppdateringer på e-post
            </p>
          </div>
          <Switch
            id="emailNotifications"
            checked={localEmailState}
            onCheckedChange={(checked) => handleNotificationChange('email', checked)}
            className="data-[state=checked]:bg-orange-600"
          />
        </div>
        <div className="flex items-center justify-between space-x-4 p-3 rounded-lg hover:bg-orange-50 transition-colors">
          <div className="space-y-0.5">
            <Label htmlFor="pushNotifications" className="text-orange-900 font-medium">
              Push-varsler
            </Label>
            <p className="text-sm text-orange-600/80">
              Motta umiddelbare varsler i nettleseren
            </p>
          </div>
          <Switch
            id="pushNotifications"
            checked={localPushState}
            onCheckedChange={(checked) => handleNotificationChange('push', checked)}
            className="data-[state=checked]:bg-orange-600"
          />
        </div>
      </CardContent>
    </Card>
  );
}